import { Button, Skeleton } from 'antd';
import { observer } from 'mobx-react';
import { FC, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card, { CardButtonWrap } from 'components/Card';
import ErrorBox from 'components/ErrorBox';
import ScrollTop from 'components/ScrollTop';
import PartialTransferDetail from 'modules/Transfer/components/TransferDetail/Partial';
import TransferFrom from 'modules/Transfer/components/TransferFrom';
import { useBrokerList, useTransferInfo } from 'modules/Transfer/hooks';
import store from 'modules/Transfer/store';
import { BrokerData, TransferMethodEnum, TransferPropertyInfoItem } from 'modules/Transfer/type';
import { PageIdEnum, TransferFormProps } from 'modules/Transfer/withTransferForm';
import security from 'stores/security';
import { isEmptyValue } from 'utils/helper';
import { otherBrokerOption } from '../../helper';
import {
    BrokerPageFormutil,
    CheckPageFormutil,
    PartialAcatsSaveReqParamsType,
    PartialDetailsPageFormutil
} from '../../type';
import { handleCheckPageParams } from '../helper';
import { usePostTransferSave } from '../hooks';

const Partial: FC<TransferFormProps> = observer(({ getStorageDataByKey, setStorageDataByKey }) => {
    const { currentAccountId, currentAccount } = security;
    const { data } = useBrokerList({ getAllBroker: true });
    const navigate = useNavigate();
    const brokerPageData = getStorageDataByKey<BrokerPageFormutil>(PageIdEnum.BROKER);
    const checkPageData = getStorageDataByKey<CheckPageFormutil>(PageIdEnum.CHECK);
    const { broker_id: brokerId } = brokerPageData;
    const resultStorageCheckPageParams = handleCheckPageParams(checkPageData);
    const {
        broker_user_name: storageBrokerUserName,
        broker_account: storageBrokerAccount,
        files: storageFiles
    } = resultStorageCheckPageParams;
    const { temporaryCash, temporaryOptionList, temporaryStockList, temporaryFundList } =
        getStorageDataByKey<PartialDetailsPageFormutil>(PageIdEnum.PARTIAL_DETAILS);
    const shouldShowCash = !isEmptyValue(temporaryCash);
    const transferPropertyInfos = useMemo<TransferPropertyInfoItem[]>(() => {
        const mergedList = [
            ...(temporaryStockList || []),
            ...(temporaryOptionList || []),
            ...(temporaryFundList || [])
        ];

        return mergedList.map(item => ({
            symbol: item.symbol,
            quantity: parseFloat(item.quantity),
            market: 'US',
            secType: item.secType,
            longShortInd: item.long_short === 'short' ? 'S' : 'L',
            ...(item.cusipNum ? { cusipNum: item.cusipNum } : {})
        }));
    }, [temporaryStockList, temporaryOptionList, temporaryFundList]);
    const {
        data: transferInfoData,
        error: transferInfoError,
        isLoading: transferInfoLoading,
        mutate: transferInfoMutate
    } = useTransferInfo();
    const { isMutating, trigger } = usePostTransferSave({
        accountId: currentAccountId!,
        statementFiles: storageFiles
    });
    // 修改经济商
    const handleEditBroker = () => {
        store.setBrokerMode('edit');

        // 需要把broker和check页面的数据填充到对应的编辑页面中
        setStorageDataByKey(PageIdEnum.EDIT_BROKER, brokerPageData);
        setStorageDataByKey(PageIdEnum.EDIT_CHECK, checkPageData);

        navigate('../edit/broker');
    };
    // 修改转股详情
    const handleEditDetails = () => {
        store.setBrokerMode('edit');
        // 转股详情的编辑, 只有单一页面, 不涉及前后页面的交互, 所以直接跳转到对应的创建时候的页面即可
        navigate('../partial-details');
    };

    useEffect(() => {
        /**
         * 进入预览页面的时候, 将store中的brokerMode重置为create
         * 这里不设置目前也不会有问题, 因为预览页面只能是编辑和提交, 而编辑模式会切换到编辑
         * 但是为了避免后续增加内容出问题, 这里还是做一个设置
         */
        store.setBrokerMode('create');
    }, []);

    const handleSubmit = () => {
        const currentBroker: BrokerData =
            (data?.items || []).find((item: BrokerData) => item.id === brokerId) || otherBrokerOption;
        const { license: brokerLicense, account_type: brokerAccountType } = currentBroker;
        const { account_no: accountNo } = transferInfoData || {};
        // temporaryCash可能是string类型(空字符串), 需要转换为number
        const numberCash = Number(temporaryCash);

        const params: PartialAcatsSaveReqParamsType = {
            ...currentBroker,
            account_no: accountNo || '',
            real_name_en: storageBrokerUserName,
            account_type: brokerAccountType || 'Individual',
            side: 'IN',
            transfer_method: TransferMethodEnum.ACATS_PARTIAL,
            // 文档上接口接受的字段是一个string, 然后经纪公司是other的时候brokerId是null, 这时候不需要传broker_id
            broker_id: brokerId ? String(brokerId) : undefined,
            userEnteredFirm: !brokerId, // 没有实际意义,brokerId是空值的时候是true
            license: brokerLicense || 'Marsco',
            total_assets: false, // 部分转是false
            category: currentAccount!.category,
            ...resultStorageCheckPageParams,
            ...(shouldShowCash
                ? {
                      currency: 'USD',
                      // 输入框代表的是mktCashValue, 后端收到的的正数存入 mktCashValue 负数会转成正数存入 mktShortValue
                      mktCashValue: numberCash
                  }
                : {}),
            transfer_property_infos: transferPropertyInfos
        };

        trigger(params);
    };

    if (transferInfoError && !transferInfoData) {
        return (
            <Card form>
                <ErrorBox error={transferInfoError} onClick={() => transferInfoMutate()} />
            </Card>
        );
    }

    if (transferInfoLoading) {
        return (
            <Card form>
                <Skeleton active />
            </Card>
        );
    }

    return (
        <Card form>
            <ScrollTop />
            <h3 className="transfer-title">{__('Confirm Information')}</h3>
            <TransferFrom
                linkOptions={{
                    onClickLink: handleEditBroker,
                    text: __('Edit')
                }}
                brokerInfo={{
                    brokerId: brokerId,
                    holderName: storageBrokerUserName,
                    account: storageBrokerAccount
                }}
            />
            <PartialTransferDetail
                onEditDetails={handleEditDetails}
                transferPropertyInfos={transferPropertyInfos}
                cashAmount={temporaryCash}
            />
            <CardButtonWrap className="mt-5">
                <Button size="large" type="primary" loading={isMutating} onClick={handleSubmit}>
                    {__('Submit')}
                </Button>
            </CardButtonWrap>
        </Card>
    );
});

export default Partial;
